import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

// components
import PdpSharedShippingModalLoader from '../PdpSharedModals/PdpSharedShippingModal/PdpSharedShippingModalLoader';
import { viewDetails } from '../pdpMessages';
import { viewQuotes, requestQuotes, viewAndRequestQuotes } from '../messages/pdpShippingMessages';

// helpers
import tracking from 'dibs-tracking';
import { buildShippingDetails } from '../helpers/shippingHelpers';
import get from 'lodash.get';

export class PdpSharedDetailShipping extends React.Component {
    constructor(props) {
        super(props);

        this.state = { modalOpen: false };
        this.openShippingModal = this.openShippingModal.bind(this);
        this.closeShippingModal = this.closeShippingModal.bind(this);
    }

    openShippingModal(e) {
        e.preventDefault();

        this.setState({ modalOpen: true }, () => {
            const { hasQuote } = this.getShippingDetails();
            const canRequestShippingQuote = this.getCanRequestShippingQuote();

            const trackingLabels = ['pdp description'];
            trackingLabels.push(`${hasQuote ? 'shipping' : 'no shipping'} quote provided`);
            if (canRequestShippingQuote) {
                trackingLabels.push('custom quote request available');
            }

            tracking.trackEvent({
                category: 'product',
                action: 'view shipping details',
                label: trackingLabels.join('|'),
            });
        });
    }

    closeShippingModal() {
        this.setState({ modalOpen: false });
    }

    getShippingDetails() {
        const { currency, localDeliveryOptions, domesticDeliveryOptions, viewer } = this.props;
        return buildShippingDetails({
            currency,
            localDeliveryOptions,
            domesticDeliveryOptions,
            shipmentQuotes: get(viewer, 'shipmentQuotes'),
            relayContext: this.context.relay,
        });
    }

    /**
     * only trade users can request a shipping quote
     * (unless enabled for all logged in users)
     */
    getCanRequestShippingQuote() {
        const { viewer, user } = this.props;
        // return false for all logged out users
        if (!user) {
            return false;
        }
        // if enabled return true for logged in users
        if (viewer.requestForAllEnabled) {
            return true;
        }
        // only members of the trade can request
        return !!user.isVerifiedTrade;
    }

    render() {
        const { currency, itemId, userId, relay } = this.props;
        const canRequestShippingQuote = this.getCanRequestShippingQuote();
        const { output, handling, hasQuote, additional } = this.getShippingDetails();

        if (!output) {
            return null;
        }

        // link cta
        let linkCta = viewDetails;
        if (hasQuote && canRequestShippingQuote) {
            linkCta = viewAndRequestQuotes;
        } else if (hasQuote) {
            linkCta = viewQuotes;
        } else if (canRequestShippingQuote) {
            linkCta = requestQuotes;
        }

        return (
            <span data-tn="pdp-spec-shipping">
                {/* Shipping Details */}
                {!!output && <p>{output}</p>}
                {!!handling && <p>{handling}</p>}
                {!!additional && <p>({additional})</p>}

                {/* Shipping Modal Link / Modal */}
                <a href="#shipping-modal" onClick={this.openShippingModal}>
                    {linkCta}
                </a>
                <PdpSharedShippingModalLoader
                    currency={currency}
                    userId={userId}
                    itemId={itemId}
                    environment={relay.environment}
                    canRequestShippingQuote={canRequestShippingQuote}
                    hasQuote={hasQuote}
                    isOpened={this.state.modalOpen}
                    closeShippingModal={this.closeShippingModal}
                    trackingLocation="pdp description"
                />
            </span>
        );
    }
}

PdpSharedDetailShipping.propTypes = {
    currency: PropTypes.string,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    localDeliveryOptions: PropTypes.object,
    domesticDeliveryOptions: PropTypes.object,
    relay: PropTypes.object.isRequired,
};

PdpSharedDetailShipping.contextTypes = {
    relay: PropTypes.object.isRequired,
};

export default createFragmentContainer(PdpSharedDetailShipping, {
    viewer: graphql`
        fragment PdpSharedDetailShipping_viewer on Viewer {
            requestForAllEnabled: isEnabled(key: "enableRequestQuoteForAllLoggedIn")
            shipmentQuotes(itemId: $itemId, searchView: PDP, first: 10) {
                ...shippingHelpers_shipmentQuotesConnection
            }
        }
    `,
    user: graphql`
        fragment PdpSharedDetailShipping_user on User {
            isVerifiedTrade
        }
    `,
    localDeliveryOptions: graphql`
        fragment PdpSharedDetailShipping_localDeliveryOptions on ShipmentQuoteConnection {
            ...shippingHelpers_shipmentQuoteConnection
        }
    `,
    domesticDeliveryOptions: graphql`
        fragment PdpSharedDetailShipping_domesticDeliveryOptions on ShipmentQuoteConnection {
            ...shippingHelpers_shipmentQuoteConnection
        }
    `,
});
